import React, { useState, useCallback } from 'react';
import { BookOpen } from 'lucide-react';
import HelpSection from '../components/HelpSection';
import { HELP_SECTIONS, HELP_PAGE_STRINGS } from '../data/helpContent';
import type { LanguageCode } from '../types';
import { LANGUAGE_LABELS } from '../types';

// =============================================================================
// CONSTANTS
// =============================================================================

const RTL_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF'];
const LANG_OPTIONS: LanguageCode[] = ['eng', 'fa-AF', 'ps-AF'];

const STORAGE_KEY = 'help-guide-lang';

function getInitialLang(): LanguageCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'eng' || stored === 'fa-AF' || stored === 'ps-AF') return stored;
  } catch { /* localStorage unavailable */ }
  return 'eng';
}

// =============================================================================
// COMPONENT
// =============================================================================

const Help: React.FC = () => {
  const [guideLang, setGuideLang] = useState<LanguageCode>(getInitialLang);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const isRtl = RTL_LANGUAGES.includes(guideLang);
  const strings = HELP_PAGE_STRINGS[guideLang];

  const handleLangChange = useCallback((lang: LanguageCode) => {
    setGuideLang(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, []);

  const toggleSection = useCallback((id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const scrollToSection = useCallback((id: string) => {
    // Expand the section first
    setExpandedSections(prev => new Set(prev).add(id));
    // Scroll after a tick so the DOM updates
    requestAnimationFrame(() => {
      const el = document.getElementById(`help-section-${id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <div className="animate-fade-in h-full overflow-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">
              {strings.pageTitle}
            </h1>
          </div>

          {/* Language pills */}
          <div className="flex gap-1 p-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
            {LANG_OPTIONS.map(lang => (
              <button
                key={lang}
                onClick={() => handleLangChange(lang)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  guideLang === lang
                    ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm'
                    : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                }`}
              >
                {LANGUAGE_LABELS[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* ── Main layout: TOC + Content ───────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-6">

          {/* TOC — horizontal pills on mobile, sticky sidebar on lg+ */}
          <nav className="lg:w-56 flex-shrink-0">
            <div className="lg:sticky lg:top-6">
              <p className="hidden lg:block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-3 px-1">
                {strings.tocLabel}
              </p>

              {/* Mobile: horizontal scrollable pills */}
              <div className="flex lg:hidden gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                {HELP_SECTIONS.map(section => {
                  const SIcon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                    >
                      <SIcon className="h-3.5 w-3.5" />
                      {section.content[guideLang].title}
                    </button>
                  );
                })}
              </div>

              {/* Desktop: vertical list */}
              <ul className="hidden lg:flex flex-col gap-0.5">
                {HELP_SECTIONS.map(section => {
                  const SIcon = section.icon;
                  const isActive = expandedSections.has(section.id);
                  return (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 text-sm rounded-md transition-colors ${
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                            : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                        }`}
                      >
                        <SIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{section.content[guideLang].title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          {/* Content sections */}
          <div
            className="flex-1 min-w-0 space-y-3"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            {HELP_SECTIONS.map(section => {
              const c = section.content[guideLang];
              return (
                <HelpSection
                  key={section.id}
                  id={section.id}
                  icon={section.icon}
                  title={c.title}
                  summary={c.summary}
                  bullets={c.bullets}
                  tips={c.tips}
                  tipsLabel={strings.tipsLabel}
                  isExpanded={expandedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
